import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useId } from 'react';
import { useForm } from 'react-hook-form';

import { Dialog, isCancelledSubmit } from '@shared/ui/dialog';

import { Field, FIELD_CONTROL_CLASS } from '../_shared/components';
import { composeCallbackEndpoint, feeBasisPointsToPercent } from '../_shared/constants';
import { sharedCopy } from '../_shared/copy';
import type { PaymentGateway } from '../_shared/types';
import { copy } from './copy';
import { gatewayFormSchema, type GatewayFormValues } from './forms.schema';

/**
 * Register a provider, or edit the one already registered. One dialog, two
 * modes, three fields.
 *
 * **The credential is not a field here.** It is a secret with no read path from
 * any surface, so it has its own action in the row menu and its own dialog; a
 * password box beside a display name invites an operator to treat the two as the
 * same kind of value.
 *
 * The callback field is Latin and says so: an explicit `dir="ltr"` while its
 * label and hint stay Arabic, because without it the address flips visually
 * inside a right-to-left field and the operator compares it, character by
 * character, against the one in the provider's console.
 */
export function GatewayDialog({
  open,
  gateway,
  busy,
  formError,
  onSubmit,
  onCancel,
}: {
  open: boolean;
  /** `null` in register mode. */
  gateway: PaymentGateway | null;
  busy: boolean;
  formError: string | null;
  onSubmit: (values: GatewayFormValues) => void;
  onCancel: () => void;
}) {
  const headingId = useId();

  const form = useForm<GatewayFormValues>({
    resolver: zodResolver(gatewayFormSchema),
    defaultValues: { name: '', callbackEndpoint: '', feePercent: '' },
  });

  const { register, handleSubmit, reset, formState } = form;

  // One element serves the whole table, so it is seeded from the row that opened
  // it every time — never left holding the previous provider's endpoint.
  useEffect(() => {
    if (!open) return;

    reset({
      name: gateway?.display_name ?? '',
      callbackEndpoint: gateway ? composeCallbackEndpoint(gateway.base_url, gateway.callback_path) : '',
      feePercent: gateway ? String(feeBasisPointsToPercent(gateway.fee_bps)) : '0',
    });
  }, [open, gateway, reset]);

  return (
    <Dialog
      open={open}
      labelledBy={headingId}
      className="max-w-[34rem]"
      onClose={(returnValue) => {
        if (returnValue !== 'save') onCancel();
      }}
    >
      <form
        method="dialog"
        onSubmit={(event) => {
          // «ألغِ» is a submit too — see `isCancelledSubmit`. Left alone, it lets
          // `method="dialog"` close the element instead of saving on the way out.
          if (isCancelledSubmit(event)) return;
          event.preventDefault();
          void handleSubmit(onSubmit)(event);
        }}
        className="flex flex-col gap-4"
      >
        <div>
          <h2 id={headingId} className="text-h3">
            {gateway ? copy.dialog.editTitle : copy.dialog.createTitle}
          </h2>
          <p className="mb-0 mt-1 max-w-none text-xs text-ink-muted">{copy.dialog.context}</p>
        </div>

        {formError ? (
          <p role="alert" className="mb-0 max-w-none text-sm text-danger">
            {formError}
          </p>
        ) : null}

        <Field
          label={copy.dialog.nameLabel}
          hint={copy.dialog.nameHint}
          error={formState.errors.name?.message}
        >
          {(id, describedBy, invalid) => (
            <input
              id={id}
              type="text"
              aria-describedby={describedBy}
              aria-invalid={invalid}
              aria-required
              className={FIELD_CONTROL_CLASS}
              {...register('name')}
            />
          )}
        </Field>

        <Field
          label={copy.dialog.callbackLabel}
          hint={copy.dialog.callbackHint}
          error={formState.errors.callbackEndpoint?.message}
        >
          {(id, describedBy, invalid) => (
            <input
              id={id}
              type="url"
              dir="ltr"
              inputMode="url"
              spellCheck={false}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              aria-required
              className={`mono ${FIELD_CONTROL_CLASS}`}
              {...register('callbackEndpoint')}
            />
          )}
        </Field>

        <Field
          label={copy.dialog.feeLabel}
          hint={copy.dialog.feeHint}
          error={formState.errors.feePercent?.message}
        >
          {(id, describedBy, invalid) => (
            <input
              id={id}
              type="text"
              inputMode="decimal"
              dir="ltr"
              aria-describedby={describedBy}
              aria-invalid={invalid}
              aria-required
              className={`mono ${FIELD_CONTROL_CLASS}`}
              {...register('feePercent')}
            />
          )}
        </Field>

        <div className="flex flex-wrap gap-2">
          <button
            type="submit"
            value="cancel"
            formNoValidate
            onClick={onCancel}
            className="rounded-sm border border-border-strong px-5 py-3 text-sm text-ink hover:bg-hover-bg"
          >
            {sharedCopy.action.cancel}
          </button>

          <button
            type="submit"
            value="save"
            disabled={busy}
            className="rounded-sm bg-accent px-5 py-3 text-sm text-accent-ink hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-45"
          >
            {sharedCopy.action.save}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
